import { CollectiblesList } from '@/components/collectibles/CollectiblesList';
import type { OwnerScopeProps } from '@/components/shared/ownerScope';
import type { Collectible } from '@/config/objects/collectibles';
import type { PlacedCollectible } from '@/config/objects/placements';
import {
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormFieldGroup,
  Input,
  Skeleton,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Package, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

/**
 * Owner-scoped manager for library collectibles. Serves both events
 * (`apiBase=/events/:id`, `keyPrefix='Event'` — query keys and URLs are
 * byte-identical to the original EventCollectiblesTab) and templates
 * (`apiBase=/templates/:id`, `keyPrefix='Template'`).
 */
export function CollectiblesManager({
  ownerId,
  apiBase,
  keyPrefix,
  structureEditable,
}: OwnerScopeProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editing, setEditing] = useState<PlacedCollectible | null>(null);
  const [pendingRemove, setPendingRemove] = useState<PlacedCollectible | null>(
    null,
  );

  const collectiblesKey = [`${keyPrefix}Collectibles`, ownerId];
  const ownerNoun = keyPrefix === 'Event' ? 'event' : 'template';

  const { data: response, isLoading } = useQuery<{
    success: boolean;
    data: PlacedCollectible[];
  }>({
    queryKey: collectiblesKey,
    queryFn: () => apiClient.get(`${apiBase}/collectibles`),
    enabled: !!ownerId,
  });
  const collectibles = response?.data || [];

  const addMutation = useMutation({
    mutationFn: async (libraryCollectibleId: string) => {
      return apiClient.post(`${apiBase}/collectibles`, {
        libraryCollectibleId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectiblesKey });
      toast.success('Collectible added');
      setShowAddDialog(false);
    },
    onError: (err: Error) =>
      toast.error(err?.message || 'Failed to add collectible'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      collectibleId,
      data,
    }: {
      collectibleId: string;
      data: Record<string, unknown>;
    }) => {
      return apiClient.patch(
        `${apiBase}/collectibles/${collectibleId}`,
        data,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectiblesKey });
      toast.success('Collectible updated');
      setEditing(null);
    },
    onError: (err: Error) =>
      toast.error(err?.message || 'Failed to update collectible'),
  });

  const removeMutation = useMutation({
    mutationFn: async (collectibleId: string) => {
      return apiClient.delete(
        `${apiBase}/collectibles/${collectibleId}`,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectiblesKey });
      toast.success('Collectible removed');
      setPendingRemove(null);
    },
    onError: (err: Error) =>
      toast.error(err?.message || 'Failed to remove collectible'),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Collectibles</h2>
          <p className="text-sm text-muted-foreground">
            Hidden objects teams pick up by completing challenges. Can gate
            other challenges.
          </p>
        </div>
        {structureEditable && (
          <Button
            onClick={() => setShowAddDialog(true)}
            size="sm"
            icon={Plus}
          >
            Add from Library
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      )}

      {!isLoading && collectibles.length === 0 && (
        <EmptyState
          icon={<Package className="h-8 w-8" />}
          title="No collectibles configured"
          description={
            structureEditable
              ? 'Add collectibles from your library, then link them to challenges as rewards or unlock requirements.'
              : `This ${ownerNoun} has no collectibles configured.`
          }
          action={
            structureEditable
              ? { label: 'Add from Library', onClick: () => setShowAddDialog(true), icon: Plus }
              : undefined
          }
        />
      )}

      {collectibles.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {collectibles.map((c) => (
            <Card key={c.id} className="gap-0 rounded-xl border bg-card py-0 shadow-sm">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-3">
                  {c.iconUrl ? (
                    <img
                      src={c.iconUrl}
                      alt={c.name}
                      className="h-10 w-10 rounded-lg object-cover shrink-0"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Package className="h-5 w-5 text-primary" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-sm truncate">{c.name}</h3>
                    <p className="text-xs text-muted-foreground">
                      {c.totalStock === 0
                        ? 'Unlimited stock'
                        : `${c.totalStock} total stock`}
                    </p>
                  </div>
                </div>

                {structureEditable && (
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setEditing({ ...c })}
                    >
                      <Pencil className="h-3 w-3" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs text-destructive hover:text-destructive"
                      onClick={() => setPendingRemove(c)}
                    >
                      <Trash2 className="h-3 w-3" />
                      Remove
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add from library dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="!w-[90vw] !max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Add Collectible from Library</DialogTitle>
            <DialogDescription>
              Select a collectible to add to this {ownerNoun}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto -mx-6 px-6">
            <CollectiblesList
              onItemClick={(c: Collectible) => addMutation.mutate(c.id)}
              className="[&_.crud-list-header]:hidden"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog
        open={!!editing}
        onOpenChange={(open) => !open && setEditing(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit "{editing?.name}"</DialogTitle>
            <DialogDescription>
              Per-{ownerNoun} overrides for this collectible
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <FormFieldGroup name="name" label="Name">
                <Input
                  value={editing.name}
                  onChange={(e) =>
                    setEditing({ ...editing, name: e.target.value })
                  }
                />
              </FormFieldGroup>
              <FormFieldGroup
                name="totalStock"
                label="Total Stock"
                description="0 = unlimited (display-only in v1)"
              >
                <Input
                  type="number"
                  min={0}
                  value={editing.totalStock}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      totalStock: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </FormFieldGroup>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="ghost" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={() =>
                    updateMutation.mutate({
                      collectibleId: editing.id,
                      data: {
                        name: editing.name,
                        totalStock: editing.totalStock,
                      },
                    })
                  }
                  loading={updateMutation.isPending}
                >
                  Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove confirmation */}
      <ConfirmDialog
        open={!!pendingRemove}
        onOpenChange={(open) => !open && setPendingRemove(null)}
        title="Remove Collectible"
        description={`Are you sure you want to remove "${pendingRemove?.name}" from this ${ownerNoun}? Teams who already collected it will keep their inventory.`}
        confirmText="Remove"
        variant="destructive"
        loading={removeMutation.isPending}
        onConfirm={() => {
          if (pendingRemove) removeMutation.mutate(pendingRemove.id);
        }}
      />
    </div>
  );
}
